import React, { lazy, Suspense, useCallback, useEffect, useMemo } from 'react';
import {
  useLocation,
  useNavigate,
  useSearchParams,
  Routes,
  Route,
} from "react-router-dom";
import {
  Box,
  Container,
} from '@mui/material';
import { useAtomValue } from 'jotai';

import { Nav } from '../../components/Nav';
import { Layout } from '../../components/Layout';
import { Feed } from '../Feed';
import { Short } from '../Short';
import { NotFound } from '../NotFound';
import { UserRoles } from '../../types';
import { StateAuth, Context, useAuth } from '../../context/auth';
import { innerHeightAtom } from '../../atoms/window';
import { isMobileAtom } from '../../atoms';
import { MobileFeed } from '../MobileFeed';
import styles from './style';

// A screen downloaded on its first opening: a visitor of the feed does not load the admin, the forms...
// A tab opened before a deploy asks a file of the old build (404): reloaded once, it gets the new one.
const RELOADED_KEY = 'screen_reloaded';
const lazyScreen = (load: () => Promise<React.ComponentType<any>>) => lazy(() => load()
  .then((Screen) => {
    try { sessionStorage.removeItem(RELOADED_KEY); } catch (e) { /* storage blocked */ }
    return { default: Screen };
  })
  .catch((err) => {
    let reloaded = true;
    try {
      reloaded = !!sessionStorage.getItem(RELOADED_KEY);
      sessionStorage.setItem(RELOADED_KEY, '1');
    } catch (e) { /* storage blocked: no reload loop */ }
    if (!reloaded) {
      window.location.reload();
      return new Promise<never>(() => undefined);
    }
    throw err;
  }));

const ForgotPassword = lazyScreen(() => import('../ForgotPassword').then((m) => m.ForgotPassword));
const AdminRankings = lazyScreen(() => import('../AdminRankings').then((m) => m.AdminRankings));
const AdminVideos = lazyScreen(() => import('../AdminVideos').then((m) => m.AdminVideos));
const Dashboard = lazyScreen(() => import('../Dashboard').then((m) => m.Dashboard));
const Installs = lazyScreen(() => import('../Installs').then((m) => m.Installs));
const MyVideos = lazyScreen(() => import('../MyVideos').then((m) => m.MyVideos));
const PlayerProfile = lazyScreen(() => import('../PlayerProfile').then((m) => m.PlayerProfile));
const Ranking = lazyScreen(() => import('../Ranking').then((m) => m.Ranking));
const Notifications = lazyScreen(() => import('../Notifications').then((m) => m.Notifications));
const Profile = lazyScreen(() => import('../Profile').then((m) => m.Profile));
const Reports = lazyScreen(() => import('../Reports').then((m) => m.Reports));
const Users = lazyScreen(() => import('../Users').then((m) => m.Users));
const Terms = lazyScreen(() => import('../Terms').then((m) => m.Terms));
const Privacy = lazyScreen(() => import('../Privacy').then((m) => m.Privacy));
const Contact = lazyScreen(() => import('../Contact').then((m) => m.Contact));
const Login = lazyScreen(() => import('../Login').then((m) => m.Login));
const SignUp = lazyScreen(() => import('../SignUp').then((m) => m.SignUp));

const NotConnectedRoutes = [
  '/login',
  '/signup',
  '/forgotpassword'
]

// Public pages, open to anyone: the legal pages are also the links of the app (soskills.fr/terms…).
const PublicRoutes = [
  '/contact',
  '/privacy',
  '/terms',
];

// Pages of the connected space, shown with the top bar + the side nav.
const AdminRoutes = [
  '/dashboard',
  '/installs',
  '/videos',
  '/rankings',
  '/users',
  '/reports',
  '/notifications',
];

const UserRoutes = [
  '/my-videos',
  '/profile',
];

// Pages a visitor can open on a computer (the actions ask to log in).
const GuestRoutes = [
  '/feed',
  '/ranking',
];

// Pages of the connected space for both roles.
const SharedRoutes = [
  '/feed',
  '/ranking',
];

// Shared link of a video: /short/:codeshare.
const SHORT_PREFIX = '/short/';

// Profile of a player, opened from the feed: /u/:userId.
const PROFILE_PREFIX = '/u/';

const isSharedRoute = (pathname: string) => (
  SharedRoutes.includes(pathname) || pathname.startsWith(PROFILE_PREFIX)
);

const NavigationRoot: React.FC<{ lang?: string, tz?: string }> = React.memo((props) => {
  const {
    logout,
    redirectToApp,
    user,
  } = useAuth() as Context;
  const isMobile = useAtomValue(isMobileAtom);
  const innerHeight = useAtomValue(innerHeightAtom)
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // A user (not an admin) on a phone: the connected space is made for a computer, he gets the
  // feed of the phone (read only, the actions open the app).
  const isMobileUser = user.isConnected === StateAuth.CONNECTED && isMobile && user.role !== UserRoles.ADMIN;
  // An admin on a phone only gets the figures of the dashboard.
  const isMobileAdmin = user.isConnected === StateAuth.CONNECTED && isMobile && user.role === UserRoles.ADMIN;

  const HomeToRender = useCallback(() => {
    // A connected user is redirected to his space, a visitor on a computer to the feed.
    if ((user.isConnected === StateAuth.CONNECTED && !isMobileUser) || !isMobile) {
      return null;
    }
    return <MobileFeed
      isMobile={isMobile}
      user={user}
      innerHeight={innerHeight}
    />;
  }, [
    innerHeight,
    isMobile,
    isMobileUser,
    user,
  ]);

  const getMarginBox = useCallback(() => {
    let rem = '3.75rem';
    if (innerHeight < 660 && user.os === 'ios') {
      rem = '2.75rem';
    } else if (innerHeight < 660 && user.os === 'android') {
      rem = '2.7rem';
    } else if (innerHeight >= 660 && innerHeight <= 734) {
      rem = '3.3rem';
    } else if (innerHeight >= 735) {
      rem = '2.8rem';
    }
    return rem;
  }, [innerHeight, user.os]);

  // On a computer a visitor gets the feed (the actions ask to log in). The feed is not made
  // for a phone yet: there he keeps the home page.
  const isGuestFeed = user.isConnected === StateAuth.NOT_CONNECTED && !isMobile;

  useEffect(() => {
    if (isMobileAdmin) {
      if (
        location.pathname === '/'
        || isSharedRoute(location.pathname)
        || UserRoutes.includes(location.pathname)
        || (AdminRoutes.includes(location.pathname) && location.pathname !== '/dashboard')
        || NotConnectedRoutes.includes(location.pathname)
      ) {
        navigate('/dashboard', { replace: true });
      }
    } else if (isMobileUser) {
      if (
        isSharedRoute(location.pathname)
        || UserRoutes.includes(location.pathname)
        || AdminRoutes.includes(location.pathname)
        || NotConnectedRoutes.includes(location.pathname)
      ) {
        navigate('/', { replace: true });
      }
    } else if (isGuestFeed && location.pathname === '/') {
      navigate('/feed', { replace: true });
    } else if (
      user.isConnected === StateAuth.NOT_CONNECTED
      && isMobile
      && (location.pathname === '/feed' || location.pathname === '/signup')
    ) {
      // On a phone the sign up happens in the app: the read only feed offers to open it.
      navigate('/', { replace: true });
    } else if (
      user.isConnected === StateAuth.NOT_CONNECTED
      && location.pathname !== '/'
      && !(isGuestFeed && GuestRoutes.includes(location.pathname))
      && location.pathname !== '/login'
      && location.pathname !== '/signup'
      && location.pathname !== '/forgotpassword'
      && !PublicRoutes.includes(location.pathname)
      && !location.pathname.includes('/short/')
    ) {
      // A protected page: the visitor feed on a computer (its popup to log in), the login on a phone.
      navigate(isGuestFeed ? '/feed' : '/login', { replace: true });
    } else if (
      user.isConnected === StateAuth.CONNECTED
      && (
        location.pathname === '/login'
        || location.pathname === '/signup'
        || location.pathname === '/forgotpassword'
      )
    ) {
      navigate('/', { replace: true });
    } else if (user.isConnected === StateAuth.CONNECTED && user.role === UserRoles.ADMIN) {
      if (
        location.pathname === '/'
        || location.pathname === '/admin'
        || UserRoutes.includes(location.pathname)
      ) {
        navigate('/dashboard', { replace: true });
      }
    } else if (user.isConnected === StateAuth.CONNECTED) {
      if (
        location.pathname === '/'
        || location.pathname === '/admin'
        || AdminRoutes.includes(location.pathname)
      ) {
        navigate('/feed', { replace: true });
      }
    }
  }, [
    isGuestFeed,
    isMobile,
    isMobileAdmin,
    isMobileUser,
    navigate,
    location.pathname,
    user,
  ]);

  const getClassNameBox = useCallback(() => {
    let str = 'app-mobile';
    if (innerHeight < 660 && user.os === 'ios') {
      str = 'app-mobile-little-x2-ios';
    } else if (innerHeight < 660 && user.os === 'android') {
      str = 'app-mobile-little-x2-android';
    } else if (innerHeight >= 660 && innerHeight <= 734) {
      str = 'app-mobile-little';
    } else if (innerHeight >= 735) {
      str = 'app-mobile';
    }
    return str;
  }, [innerHeight, user.os]);

  const boxClass = useMemo(() => {
    if (
      isMobile &&
      (
        (location.pathname.includes('/short/') && !searchParams.get('redirect'))
        || location.pathname === '/'
      )
    ) {
      return "app-short-without-footer";
    } else if (
      (isMobile && !location.pathname.includes('/short/'))
      || (searchParams.get('redirect') && searchParams.get('redirect') === '42')
    ) {
      return getClassNameBox();
    } else if (!isMobile) {
      return "app";
    }
  }, [
    getClassNameBox,
    isMobile,
    location.pathname,
    searchParams,
  ]);

  const dynamicPaddingTop = useMemo(() => {
    return isMobile
      ? ((!location.pathname.includes('/short/') && location.pathname !== '/')
        || (searchParams.get('redirect') && searchParams.get('redirect') === '42'))
        ? getMarginBox()
        : '0rem'
      : '4.1rem'
  }, [
    isMobile,
    getMarginBox,
    location.pathname,
    searchParams,
  ]);

  const disableGutters = useMemo(() => (
    (location.pathname === '/' && isMobile)
    || location.pathname.includes('/short/')
  ), [isMobile, location.pathname]);

  // Pages a connected user leaves at once for his space (the redirects above).
  const isConnectedRedirect = user.isConnected === StateAuth.CONNECTED && (
    location.pathname === '/'
    || location.pathname === '/admin'
    || NotConnectedRoutes.includes(location.pathname)
    || (user.role === UserRoles.ADMIN && UserRoutes.includes(location.pathname))
    || (user.role !== UserRoles.ADMIN && AdminRoutes.includes(location.pathname))
  );

  // On a computer everything is in the connected space, the old top bar is only for the phone: the feed and
  // the shared links (its video first), the public pages, the login / sign up / forgot password of a visitor
  // and every page of a connected user (a wrong address shows "page not found" there too).
  const isDesktopSpace = !isMobile && user.isConnected !== StateAuth.LOADING && (
    GuestRoutes.includes(location.pathname)
    || location.pathname.startsWith(SHORT_PREFIX)
    || PublicRoutes.includes(location.pathname)
    || (user.isConnected === StateAuth.NOT_CONNECTED && NotConnectedRoutes.includes(location.pathname))
    || (user.isConnected === StateAuth.CONNECTED && !isConnectedRedirect)
  );

  // On a phone, an admin only gets the figures of the dashboard.
  const isConnectedSpace = isDesktopSpace || (isMobileAdmin && location.pathname === '/dashboard');

  if (isConnectedSpace) {
    return (
      <Layout user={user} logout={logout}>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/feed" element={<Feed />} />
            {/* The same feed: going to /feed from it (another tab, filters) keeps the screen. */}
            <Route path={`${SHORT_PREFIX}:codeshare`} element={<Feed />} />
            <Route path="/ranking" element={<Ranking />} />
            <Route path={`${PROFILE_PREFIX}:userId`} element={<PlayerProfile />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/contact" element={<Contact />} />
            {user.isConnected === StateAuth.NOT_CONNECTED && (
              <>
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/forgotpassword" element={<ForgotPassword />} />
              </>
            )}
            <Route path="*" element={<NotFound />} />
            {user.role === UserRoles.ADMIN ? (
              <>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/installs" element={<Installs />} />
                <Route path="/videos" element={<AdminVideos />} />
                <Route path="/rankings" element={<AdminRankings />} />
                <Route path="/users" element={<Users />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/notifications" element={<Notifications />} />
              </>
            ) : (
              <>
                <Route path="/my-videos" element={<MyVideos />} />
                <Route path="/profile" element={<Profile />} />
              </>
            )}
          </Routes>
        </Suspense>
      </Layout>
    );
  }

  // On a computer "/" is never shown: the visitor goes to the feed, a connected user to his space.
  // A visitor keeps the pages he can open without an account (login, sign up, forgot password, legal
  // pages, shared links): any other page sends him to the feed. Nothing is rendered until the redirect:
  // the old pages flashed before.
  const isGuestPage = NotConnectedRoutes.includes(location.pathname)
    || GuestRoutes.includes(location.pathname)
    || PublicRoutes.includes(location.pathname)
    || location.pathname.includes('/short/');
  const isDesktopRedirect = !isMobile && ((isGuestFeed && !isGuestPage) || isConnectedRedirect);
  if (isDesktopRedirect) {
    return null;
  }

  return (
    <div
      style={{
        paddingTop: dynamicPaddingTop,
        height: 'calc(var(--app-height));',
      }}
    >
      {user.isConnected !== StateAuth.LOADING && (
        <div style={styles.fullHeight}>
          <Nav
            redirectToApp={redirectToApp}
            isConnected={user.isConnected}
            os={user.os}
            innerHeight={innerHeight}
            user={user}
            logout={logout}
          />
          <Container maxWidth="lg" disableGutters={disableGutters}>
            <Box className={boxClass}>
              <div style={styles.fullHeight}>
                <Suspense fallback={null}>
                  <Routes>
                    <Route
                      path="/short/:codeshare"
                      element={
                        <Short
                          isMobile={isMobile}
                          user={user}
                          innerHeight={innerHeight}
                        />
                      }
                    />
                    <Route
                      index
                      element={HomeToRender()}
                    />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/contact" element={<Contact />} />
                    {user.isConnected === StateAuth.NOT_CONNECTED && (
                      <>
                        <Route path="/login" element={<Login />} />
                        <Route path="/signup" element={<SignUp />} />
                        <Route path="/forgotpassword" element={<ForgotPassword />} />
                      </>
                    )}
                    {!NotConnectedRoutes.some((r) => r === location.pathname) && (
                      <Route path="*" element={<NotFound />} />
                    )}
                  </Routes>
                </Suspense>
              </div>
            </Box>
          </Container>
        </div>
      )}
    </div>
  );
});

export default NavigationRoot;
